import { Component, OnInit, ChangeDetectorRef, ViewChild } from '@angular/core';
import { MatSnackBar } from '@angular/material/snack-bar';
import { LocalStorageService } from 'ngx-webstorage';
import { AgrimotorService } from './agrimotor.service';
import { AgrimotorFleetExperienceComponent } from './fleet-experience/fleet-experience.component';
import { ActivatedRoute, Router } from "@angular/router";
import { UserInfoService } from '../../services/user-info.service';
import { Agrimotor } from 'src/app/model/quote/agrimotor/agrimotor.model';
import { environment } from '../../../environments/environment';

@Component({
  selector: 'app-agrimotor',
  templateUrl: './agrimotor.component.html',
  styleUrls: ['./agrimotor.component.css', '../quote.component.css']
})
export class AgrimotorComponent implements OnInit {
  selectedIndex: number = 0;
  staticData;
  modelObj = new Agrimotor();
  url_id = this.route.snapshot.params.id;
  quote_type: number = this.route.snapshot.params.quote_type;
  authDetails: any = '';

  @ViewChild(AgrimotorFleetExperienceComponent) fleetExperience: AgrimotorFleetExperienceComponent;

  constructor(private agService: AgrimotorService, private route: ActivatedRoute, private sessionSt: LocalStorageService,
    private userService: UserInfoService, private cdr: ChangeDetectorRef, private router: Router,
    private _snackBar: MatSnackBar) { }

  ngOnInit(): void {
    this.userService.getUserDetails();
    this.getAccessToken();
    this.getAgrimotorData();
  }

  ngAfterViewChecked() {
    this.cdr.detectChanges();
  }

  getAccessToken() {
    let data = {
      'client_id': environment.AGRIMOTOR_CLIENT_ID,
      'client_secret': environment.AGRIMOTOR_CLIENT_SECRET,
      'grant_type': environment.AGRIMOTOR_GRANT_TYPE,
      'scope': environment.AGRIMOTOR_SCOPE,
    }
    this.agService.getAccessToken(data)
      .subscribe(
        (response: any) => {
          if (response.access_token) {
            this.authDetails = response;
            this.getStaticData(response);
          }
        },
        (error) => { }
      );
  }

  getStaticData(authDetails) {
    this.agService.getStaticData(authDetails)
      .subscribe(
        (response: any) => {
          if (response.meta) {
            this.staticData = response.meta;
          }
        },
        (error) => { }
      );
  }

  // GET AGRIMOTOR DATA (same pattern as Amlin getAmlineData)
  apiModelObj;
  getAgrimotorData(id = '') {
    let quote_id = id || this.route.snapshot.params.id;
    if (quote_id) {

      this.agService.getAgrimotorData(quote_id)
        .subscribe(
          (response: any) => {
            if (response.result) {
              let quoteData = response.result.quote_data;
              if (quoteData.quote_versions && quoteData.quote_versions.length > 0) {
                let latestVersion = quoteData.quote_versions[0];
                const application = this.agService.applicationOf(latestVersion);

                if (application) {
                  this.apiModelObj = latestVersion;
                  this.modelObj = latestVersion;
                  // An existing quote has already been through every page.
                  this.unlockUpTo(this.lastTabIndex);
                }
              }
            }
          },
          (error) => { }
        );
    }
  }

  // Called by proposer-details and drivers after save — updates modelObj AND advances tab
  modelObjUpdated(data) {
    this.modelObj = null;
    this.modelObj = data;
    this.getAgrimotorData(data.id);
    this.selectedIndex += 1;
    this.unlockUpTo(this.selectedIndex);
    this.scrollToTop();
  }

  // ─── Tab gating ────────────────────────────────────────────────────────────
  // A tab only becomes reachable once the page before it has been completed and saved,
  // so the user can't click straight through the header strip leaving mandatory fields
  // behind them. Going back to an already-completed tab stays allowed.
  //
  // Reopening a previously saved quote unlocks everything — the pages were completed
  // when it was created, and a broker legitimately needs to jump straight to Quotation.
  maxReachedIndex: number = 0;

  // The Fleet Experience tab is only rendered for fleet-rated quotes, so every tab
  // after it shifts by one. These keep the template honest about real tab indices.
  get fleetTabIndex(): number { return 1; }
  get vehiclesTabIndex(): number { return this.isFleetActivated ? 2 : 1; }
  get driversTabIndex(): number { return this.vehiclesTabIndex + 1; }
  get quotationTabIndex(): number { return this.driversTabIndex + 1; }
  get lastTabIndex(): number { return this.quotationTabIndex; }

  private unlockUpTo(index: number) {
    if (index > this.maxReachedIndex) { this.maxReachedIndex = index; }
  }

  isTabLocked(index: number): boolean {
    return index > this.maxReachedIndex;
  }

  // Without this, the page keeps whatever scroll position the user was at when they
  // clicked Proceed — since the new tab's content starts above the viewport, it looks
  // like nothing happened and the user has to manually scroll up to the tab header.
  scrollToTop() {
    setTimeout(() => window.scrollTo({ top: 0, behavior: 'smooth' }));
  }

  isFleetActivated: boolean = false;
  is_fleet(event) {
    this.isFleetActivated = event;
  }

  tabChanged(event) {
    if (this.isTabLocked(event)) { return; }
    if (!this.canLeaveFleetExperience(event)) {
      // mat-tab-group emits this before it writes the new index back through
      // [(selectedIndex)], so simply not accepting it is not enough — the tab has to be
      // put back after that write lands, or the header and the body disagree about
      // which tab is active.
      setTimeout(() => { this.selectedIndex = this.fleetTabIndex; });
      return;
    }
    this.selectedIndex = event;
    this.scrollToTop();
  }

  /**
   * Fleet Experience is mandatory on a fleet-rated quote, but every tab of a reopened
   * quote is unlocked — so clicking the header strip walked straight past its Proceed
   * button and the validation that lives there. Moving forward off the tab now runs the
   * same checks; going back to an earlier tab stays free.
   */
  private canLeaveFleetExperience(targetIndex: number): boolean {
    if (!this.isFleetActivated) { return true; }
    // Going back, or to Fleet Experience itself, is always allowed — only the tabs that
    // sit after it are out of reach while it is incomplete.
    if (targetIndex <= this.fleetTabIndex) { return true; }
    if (!this.fleetExperience || !this.fleetExperience.hasMissingRequiredFields()) { return true; }
    this.fleetExperience.showValidationErrors();
    this._snackBar.open(
      'Please complete the Fleet Experience details before moving on.',
      '', { duration: 5000, panelClass: 'text-danger' });
    return false;
  }
}
